// Industry benchmark data for ISO 27001 implementations
// Sources: Pivot Point Security (2024), Tracynar Cost Breakdown (2024),
// Advisera Knowledge Base, Elevate Consult Audit Blueprint (2026),
// Schellman ISO Certification Factors, aggregated implementation data.
//
// CONSISTENCY DESIGN: benchmark averages are derived from the SAME calculation
// engine, evaluated for a "typical organization" profile of the chosen size and
// industry (maturity 3, single location, hybrid infra, 5 IT systems, no prior
// certs, mixed 50/50 delivery at default rates). The comparison then adjusts the
// benchmark for the user's SCOPE (locations, infrastructure, IT estate) but NOT
// for their READINESS (maturity, existing certs) or DELIVERY choices (approach,
// custom rates) — those remain visible as the user's deviation from the average,
// explained by the driver chips. This guarantees the benchmark can never drift
// from the engine, and that all money is compared in the same currency.

import {
  calculateEstimate,
  getEffectiveMaturity,
  interpolateFactor,
  MATURITY_FACTORS,
  CERT_REDUCTIONS,
  FX_RATES,
  DEFAULT_INTERNAL_RATE,
  DEFAULT_CONSULTANT_RATE,
} from './calculationEngine';

// The "average organization" readiness & delivery assumptions behind every benchmark.
const STANDARD_PROFILE = {
  maturityMode: 'simple',
  overallMaturity: 3,
  locations: 1,
  itSystems: 5,
  infraType: 'hybrid',
  existingCerts: [],
  approach: 'mixed',
  internalSplit: 50,
  mixRatio: 50,
  currency: 'EUR',
};

// Modelled sector metadata (DEFAULT ASSUMPTION, display-only; not used in cost math).
// No empirical pass-rate/percentile data is published — see docs/METHODOLOGY_AUDIT.md.
const INDUSTRY_META = {
  finance:       { certMaintenanceYearly: 25000 },
  healthcare:    { certMaintenanceYearly: 22000 },
  government:    { certMaintenanceYearly: 20000 },
  energy:        { certMaintenanceYearly: 23000 },
  retail:        { certMaintenanceYearly: 18000 },
  manufacturing: { certMaintenanceYearly: 19000 },
  technology:    { certMaintenanceYearly: 20000 },
  education:     { certMaintenanceYearly: 15000 },
  telecom:       { certMaintenanceYearly: 21000 },
  insurance:     { certMaintenanceYearly: 24000 },
  logistics:     { certMaintenanceYearly: 17000 },
  other:         { certMaintenanceYearly: 18000 },
};

// Industry-specific implementation insights
const INDUSTRY_INSIGHTS = {
  finance: [
    'Financial regulators (FCA, BaFin, MAS) increasingly treat ISO 27001 as a baseline expectation — certification strengthens regulatory posture.',
    'Map existing SOX, PCI DSS or Basel III controls to Annex A to reduce duplication — where those programmes exist, part of Annex A is often already covered.',
    'Third-party risk management (A.5.19–A.5.23) is the most common audit finding in financial services due to complex vendor ecosystems.',
    'Auditor scrutiny is highest on access control (A.5.15–A.5.18) and cryptography (A.8.24) — allocate extra preparation time for evidence collection.',
  ],
  healthcare: [
    'Where HIPAA privacy and security controls already exist, mapping them to Annex A early in the gap analysis avoids duplicated implementation work.',
    'Patient data classification often maps directly to A.5.9–A.5.12 (Asset Management) — leverage existing data handling procedures.',
    'Business continuity (A.5.29–A.5.30) receives heavy auditor focus given critical healthcare service dependencies.',
    'Medical device and IoT security is an emerging audit focus area — include OT systems in scope where patient safety is impacted.',
  ],
  government: [
    'Public-sector implementations often take longer than comparable private-sector projects because of procurement cycles, stakeholder approvals and change-management processes.',
    'Existing NIST SP 800-53 or FedRAMP controls overlap substantially with ISO 27001 Annex A, so mapping them first avoids rebuilding evidence.',
    'Physical security (A.7) is often already mature in government settings, which usually reduces the effort needed in that domain.',
    'Inter-agency data sharing agreements need careful documentation during scope definition to avoid audit findings.',
  ],
  energy: [
    'OT/ICS environments add material complexity — make sure operational technology is properly scoped and covered with specialised controls.',
    'NERC CIP compliance provides a strong baseline that overlaps with a number of ISO 27001 controls.',
    'Remote facility security (A.7) is a significant effort area for geographically distributed energy infrastructure.',
    'Supply chain security (A.5.19–A.5.23) is critical given dependencies on specialized SCADA and control system vendors.',
  ],
  retail: [
    'PCI DSS compliance provides a solid foundation — a number of Annex A controls are already addressed by PCI requirements.',
    'E-commerce platforms require focused attention on A.8.25–A.8.28 (secure development lifecycle) and A.5.15–A.5.18 (access control for customer data).',
    'Seasonal workforce fluctuations make A.6 (HR security) processes particularly important to document and automate.',
    'Multi-channel customer data flows require comprehensive A.8.20–A.8.22 (communications security) controls across POS, web, and mobile.',
  ],
  manufacturing: [
    'Industrial control systems and IoT devices expand scope significantly — define clear boundaries between IT and OT environments.',
    'Quality management (ISO 9001) overlap can reduce management-system overhead if you are already certified.',
    'Physical security for production facilities often requires controls beyond standard office environments — budget accordingly.',
    'Supply chain risk is a top audit concern — document vendor assessments for all critical component and material suppliers.',
  ],
  technology: [
    'Technology organisations often start from a stronger baseline thanks to existing security culture and tooling maturity.',
    'DevSecOps practices can accelerate A.8.25–A.8.28 compliance — automated CI/CD evidence collection cuts manual audit preparation.',
    'Cloud-native architectures simplify infrastructure controls but require detailed shared responsibility documentation for auditors.',
    'Rapid release cycles need well-integrated change management that balances speed with control — auditors will verify this.',
  ],
  education: [
    'Student data protection regulations (FERPA, GDPR) overlap with ISO 27001 privacy controls — leverage existing compliance work.',
    'Decentralized IT across departments and faculties requires careful scope definition and federated governance models.',
    'Research data handling may need special classification tiers beyond standard controls — consult with principal investigators.',
    'Budget constraints are common — prioritize controls by risk assessment results to maximize security improvement per dollar spent.',
  ],
  telecom: [
    'Network infrastructure security (A.8.20–A.8.22) is the top audit focus area — document controls for all network segments and interconnects.',
    'Telecommunications companies face strict regulatory requirements (GDPR, NIS2) that overlap significantly with ISO 27001 controls.',
    'High service availability requirements mean A.5.29–A.5.30 (Business Continuity) receives extra auditor scrutiny — ensure RTO/RPO are documented.',
    'Third-party and supply chain security is critical given reliance on specialized network equipment vendors and cloud providers.',
  ],
  insurance: [
    'Insurance regulatory requirements (Solvency II, Lloyd\'s, state regulations) overlap with ISO 27001 — map controls early to reduce rework.',
    'Actuarial and claims data classification under A.5.9–A.5.12 (Asset Management) needs careful handling given data sensitivity and regulatory scrutiny.',
    'Third-party data processors (claims handlers, brokers) need thorough supplier security reviews (A.5.19–A.5.23) — plan for extended vendor assessments.',
    'Catastrophic event response scenarios must be included in business continuity planning (A.5.29–A.5.30) to satisfy both auditors and regulators.',
  ],
  logistics: [
    'IoT-connected vehicles, warehouses, and tracking systems expand the attack surface — include all networked operational assets in scope.',
    'Cross-border data flows for customs documentation require careful GDPR and data transfer controls under A.5.14.',
    'Supply chain partner portals and EDI systems are frequent audit findings — document access control for all third-party integrations.',
    'Real-time tracking systems and customer-facing platforms need clear incident response procedures to minimize disruption to time-critical operations.',
  ],
  other: [
    'Start with a thorough gap analysis against all 93 Annex A controls to identify your highest-effort domains.',
    'Engaging an experienced lead auditor for a pre-assessment (Stage 0) surfaces gaps before the certification audit.',
    'Document your risk assessment methodology early — auditors scrutinize this area regardless of industry or organization size.',
    'Consider industry-specific regulations that overlap with ISO 27001 to avoid duplicate control implementation.',
  ],
};

/**
 * Get benchmark data for a specific industry and company size.
 * Averages are produced by the calculation engine for a typical organization
 * (STANDARD_PROFILE), so they always agree with the user-facing estimates.
 * All money values are in EUR.
 */
export function getBenchmark(industry, companySize) {
  const meta = INDUSTRY_META[industry] || INDUSTRY_META.other;
  const std = calculateEstimate({ ...STANDARD_PROFILE, companySize, industry });

  const avgEffort = std.totalEffort;
  const avgCost = std.totalCost;
  const avgDurationWeeks = std.totalDurationWeeks;
  // Blended labor day rate of the standard 50/50 delivery model.
  const avgCostPerDay = Math.round(std.costBreakdown.labor / Math.max(1, std.totalEffort));

  return {
    avgEffort,
    // Effort range (±25% — real-world variance is high)
    effortRange: {
      low: Math.round(avgEffort * 0.75),
      high: Math.round(avgEffort * 1.25),
    },
    avgCost,
    // Cost range (±30% — accounts for rate differences and scope changes)
    costRange: {
      low: Math.round(avgCost * 0.70),
      high: Math.round(avgCost * 1.30),
    },
    avgDurationWeeks,
    avgCostPerDay,
    avgAuditDays: std.auditDays,
    certMaintenanceYearly: meta.certMaintenanceYearly,
    insights: INDUSTRY_INSIGHTS[industry] || INDUSTRY_INSIGHTS.other,
  };
}

const formatLevel = (m) => Math.round(m * 10) / 10;

/**
 * Compare a user's estimate against a SCOPE-AWARE benchmark.
 * The benchmark is adjusted for the user's structural scope (locations,
 * infrastructure, IT estate, workforce) but keeps average readiness (maturity 3,
 * no prior certs) and the standard delivery model (mixed 50/50, default rates).
 * The user's deviation therefore reflects their readiness and delivery choices —
 * which the driver chips explain. All money is converted to the user's currency.
 */
export function compareToBenchmark(results, config) {
  const fx = FX_RATES[config.currency] || 1;
  const convert = (v) => Math.round(v * fx);

  const base = getBenchmark(config.industry, config.companySize);

  // Scope-adjusted benchmark: the engine evaluated for an average-readiness org
  // with the user's scope. Same code path as the estimate — zero drift.
  const adjusted = calculateEstimate({
    ...STANDARD_PROFILE,
    companySize: config.companySize,
    industry: config.industry,
    locations: config.locations,
    itSystems: config.itSystems,
    infraType: config.infraType,
    employees: config.employees,
  });

  const adjEffort = adjusted.totalEffort;
  const adjCost = convert(adjusted.totalCost);
  const adjDuration = adjusted.totalDurationWeeks;

  const effortDiff = results.totalEffort - adjEffort;
  const effortDiffPct = adjEffort > 0 ? Math.round((effortDiff / adjEffort) * 100) : 0;

  const costDiff = results.totalCost - adjCost;
  const costDiffPct = adjCost > 0 ? Math.round((costDiff / adjCost) * 100) : 0;

  const durationDiff = results.totalDurationWeeks - adjDuration;
  const durationDiffPct = adjDuration > 0 ? Math.round((durationDiff / adjDuration) * 100) : 0;

  // Deviation from the model reference is a neutral analytical signal (higher is not
  // "bad", lower is not "good"), so the palette stays carbon/blue/grey.
  const getStatus = (diffPct) => {
    if (diffPct <= -20) return { key: 'benchmark.statusWellBelow', label: 'Well below model reference', color: '#2867B2', tone: 'positive' };
    if (diffPct <= -5)  return { key: 'benchmark.statusBelow', label: 'Below model reference', color: '#2867B2', tone: 'positive' };
    if (diffPct <= 5)   return { key: 'benchmark.statusInLine', label: 'In line with model reference', color: '#66707A', tone: 'neutral' };
    if (diffPct <= 20)  return { key: 'benchmark.statusAbove', label: 'Above model reference', color: '#3A4249', tone: 'caution' };
    return { key: 'benchmark.statusWellAbove', label: 'Well above model reference', color: '#171A1D', tone: 'warning' };
  };

  // Driver breakdown — which factors are pushing the estimate up/down.
  // Impact percentages come from the engine's own factor tables.
  // `key`/`params` are resolved with i18n at render time; `label` is the English fallback.
  const drivers = [];

  const userMaturity = getEffectiveMaturity(config);
  const maturityFactor = interpolateFactor(MATURITY_FACTORS, userMaturity);
  if (userMaturity <= 2.5) {
    drivers.push({
      id: 'maturity-low',
      key: 'benchmark.driverLowMaturity',
      params: { level: formatLevel(userMaturity) },
      label: `Low maturity (Level ${formatLevel(userMaturity)})`,
      impact: `+${Math.round((maturityFactor - 1.0) * 100)}%`,
      tone: 'warning',
    });
  } else if (userMaturity >= 3.5) {
    drivers.push({
      id: 'maturity-high',
      key: 'benchmark.driverHighMaturity',
      params: { level: formatLevel(userMaturity) },
      label: `High maturity (Level ${formatLevel(userMaturity)})`,
      impact: `-${Math.round((1.0 - maturityFactor) * 100)}%`,
      tone: 'positive',
    });
  }

  const locs = Math.max(1, config.locations || 1);
  if (locs > 1) {
    const locationAdj = 1 + Math.log2(locs) * 0.12;
    drivers.push({
      id: 'locations',
      key: 'benchmark.driverLocations',
      params: { count: locs },
      label: `${locs} locations`,
      impact: `+${Math.round((locationAdj - 1.0) * 100)}%`,
      tone: 'warning',
    });
  }

  if (config.infraType === 'onpremise') {
    drivers.push({ id: 'infra-onpremise', key: 'benchmark.driverOnPremise', label: 'On-premise infrastructure', impact: '+15%', tone: 'warning' });
  } else if (config.infraType === 'cloud') {
    drivers.push({ id: 'infra-cloud', key: 'benchmark.driverCloud', label: 'Cloud infrastructure', impact: '-10%', tone: 'positive' });
  }

  let certAdj = 0;
  (config.existingCerts || []).forEach(cert => { certAdj += CERT_REDUCTIONS[cert] || 0; });
  certAdj = Math.min(certAdj, 0.30);
  if (certAdj > 0) {
    drivers.push({
      id: 'existing-certs',
      key: 'benchmark.driverExistingCerts',
      label: 'Existing certifications',
      impact: `-${Math.round(certAdj * 100)}%`,
      tone: 'positive',
    });
  }

  // Delivery model: compare the user's blended labor day rate (in EUR) with the
  // standard mixed 50/50 blend at default rates.
  if (results.totalEffort > 0) {
    const standardBlended = (DEFAULT_INTERNAL_RATE + DEFAULT_CONSULTANT_RATE) / 2;
    const userBlendedEur = (results.costBreakdown.labor / fx) / results.totalEffort;
    const rateImpact = Math.round((userBlendedEur / standardBlended - 1) * 100);
    if (Math.abs(rateImpact) >= 5) {
      let label = 'Custom day rates';
      let key = 'benchmark.driverCustomRates';
      if (config.approach === 'consultant' || config.approach === 'external') {
        label = 'Consultant-led delivery';
        key = 'benchmark.driverConsultantLed';
      } else if (config.approach === 'internal') {
        label = 'Internal-led delivery';
        key = 'benchmark.driverInternalLed';
      }
      drivers.push({
        id: 'delivery-model',
        key,
        label,
        impact: `${rateImpact > 0 ? '+' : ''}${rateImpact}%`,
        tone: rateImpact > 0 ? 'warning' : 'positive',
      });
    }
  }

  return {
    benchmark: {
      ...base,
      avgCost: convert(base.avgCost),
      costRange: { low: convert(base.costRange.low), high: convert(base.costRange.high) },
      avgCostPerDay: convert(base.avgCostPerDay),
      certMaintenanceYearly: convert(base.certMaintenanceYearly),
      adjEffort,
      adjCost,
      adjDuration,
    },
    effort: {
      yours: results.totalEffort,
      avg: adjEffort,
      diff: effortDiff,
      diffPct: effortDiffPct,
      status: getStatus(effortDiffPct),
    },
    cost: {
      yours: results.totalCost,
      avg: adjCost,
      diff: costDiff,
      diffPct: costDiffPct,
      status: getStatus(costDiffPct),
    },
    duration: {
      yours: results.totalDurationWeeks,
      avg: adjDuration,
      diff: durationDiff,
      diffPct: durationDiffPct,
      status: getStatus(durationDiffPct),
    },
    drivers,
    avgAuditDays: base.avgAuditDays,
    certMaintenanceYearly: convert(base.certMaintenanceYearly),
    insights: base.insights,
  };
}
